export const MINUS = '−'

const group = (integer: string): string => integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

const isNumber = (value: number | null | undefined): value is number => typeof value === 'number' && Number.isFinite(value)

export const round = (value: number, decimals = 2): number => Number(value.toFixed(decimals))

export const formatNumber = (value: number | null | undefined, decimals = 0): string => {
    if (!isNumber(value)) return '-'

    const fixed = Math.abs(value).toFixed(decimals)
    const [integer, fraction] = fixed.split('.')
    const sign = value < 0 && Number(fixed) !== 0 ? MINUS : ''

    return `${sign}${group(integer)}${fraction ? `.${fraction}` : ''}`
}

export const formatPrice = (value: number | null | undefined): string => formatNumber(value, 2)

export const formatSigned = (value: number | null | undefined, decimals = 2): string => {
    if (!isNumber(value)) return '-'

    const fixed = Math.abs(value).toFixed(decimals)
    if (Number(fixed) === 0) return fixed

    return `${value > 0 ? '+' : MINUS}${formatNumber(Math.abs(value), decimals)}`
}

export const formatPercent = (value: number | null | undefined, decimals = 2, signed = true): string => {
    if (!isNumber(value)) return '-'

    return `${signed ? formatSigned(value, decimals) : formatNumber(value, decimals)}%`
}

export const formatCompact = (value: number | null | undefined, decimals?: number): string => {
    if (!isNumber(value)) return '-'

    const abs = Math.abs(value)
    const sign = value < 0 ? MINUS : ''

    if (abs >= 1e12) return `${sign}${(abs / 1e12).toFixed(decimals ?? 2)}T`
    if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(decimals ?? 2)}B`
    if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(decimals ?? 2)}M`
    if (abs >= 1e4) return `${sign}${(abs / 1e3).toFixed(decimals ?? 1)}K`

    return `${sign}${formatNumber(abs, 0)}`
}

export const formatSignedCompact = (value: number | null | undefined, decimals?: number): string => {
    if (!isNumber(value)) return '-'
    if (value === 0) return '0'

    return `${value > 0 ? '+' : MINUS}${formatCompact(Math.abs(value), decimals)}`
}

export const formatKes = (value: number | null | undefined, decimals?: number): string => {
    if (!isNumber(value)) return '-'

    return `KES ${formatCompact(value, decimals)}`
}

export const formatSignedKes = (value: number | null | undefined, decimals?: number): string => {
    if (!isNumber(value)) return '-'
    if (value === 0) return 'KES 0'

    return `${value > 0 ? '+' : MINUS}KES ${formatCompact(Math.abs(value), decimals)}`
}

export const formatMultiple = (value: number | null | undefined, decimals = 1): string => {
    if (!isNumber(value)) return '-'

    return `${formatNumber(value, decimals)}x`
}

export const formatLevel = (value: number | null | undefined): string => formatNumber(value, 2)

export const directionOf = (value: number | null | undefined): 'up' | 'down' | 'flat' => {
    if (!isNumber(value)) return 'flat'
    if (Math.abs(value) < 1e-9) return 'flat'

    return value > 0 ? 'up' : 'down'
}

export const toneClass = (value: number | null | undefined): string => {
    const direction = directionOf(value)
    if (direction === 'up') return 'text-up'
    if (direction === 'down') return 'text-down'

    return 'text-muted-foreground'
}
